import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../hooks/useHandleErrorFields';
import { AuthContext } from '../../../contexts/AuthContext';
import { mapAuthResponse } from '../../../app/models/Auth';
import InputField from '../../../components/InputField';
import ButtonGeneral from '../../../components/buttons/ButtonGeneral';
import SquareCheckbox from '../../../components/SquareCheckbox/SquareCheckbox';
import logo_monere from '../../../assets/images/logo_monere.png';
import '../Login/Login.css';

interface Invitation {
    email: string;
    first_name: string;
    last_name: string;
    role_display: string;
    organization_name: string;
    invited_by: string | null;
    expires_at: string | null;
    has_account: boolean;
    cases: { own: number; monere_open: number };
    branding: { name: string; logo_url: string | null; primary_color: string };
}

/** Aceptar la invitación a un estudio: con la cuenta existente o creando una. */
const AcceptInvitation: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const history = useHistory();
    const { login } = useContext(AuthContext);
    const { getInvitation, acceptInvitation, fetchingInvitation, fetchingAcceptInvitation } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();

    const [invitation, setInvitation] = useState<Invitation | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [form, setForm] = useState({ first_name: '', last_name: '', password: '', password_confirm: '', accept_terms: false });

    useEffect(() => {
        getInvitation(token, {
            onSuccess: (response) => {
                setInvitation(response.data);
                setForm((prev) => ({ ...prev, first_name: response.data.first_name, last_name: response.data.last_name }));
            },
            onError: (response) => setLoadError(response.message)
        });
    }, [token]);

    const handleAccept = () => {
        if (!invitation) return;
        if (!invitation.has_account) {
            if (form.password !== form.password_confirm) {
                setErrors({ password_confirm: ['Las contraseñas no coinciden.'] });
                return;
            }
            if (!form.accept_terms) {
                setErrors({ accept_terms: ['Debes aceptar los términos y la política de privacidad.'] });
                return;
            }
        }
        acceptInvitation(token, {
            password: form.password,
            first_name: form.first_name,
            last_name: form.last_name,
            accept_terms: invitation.has_account ? true : form.accept_terms
        }, {
            onSuccess: (response) => {
                if (login) login(mapAuthResponse(response.data.auth));
                toast.success(response.message);
                history.push('/intranet');
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => setErrors(response.data)
        });
    };

    const accent = invitation?.branding?.primary_color || '#001849';

    return (
        <div className="login-container">
            <div className="login-background"><div className="login-background-pattern" /></div>
            <div className="container position-relative" style={{ maxWidth: 560 }}>
                <div className="card shadow">
                    <div className="card-body p-4 p-md-5">
                        <div className="text-center mb-4">
                            {invitation?.branding?.logo_url ? (
                                <img src={invitation.branding.logo_url} alt={invitation.branding.name} style={{ maxHeight: 64, maxWidth: 240 }} />
                            ) : (
                                <img src={logo_monere} alt="Monere" style={{ height: 56 }} />
                            )}
                        </div>

                        {fetchingInvitation && !invitation && <div className="text-center py-4"><div className="spinner-border text-primary" /></div>}

                        {loadError && (
                            <div className="text-center">
                                <i className="fas fa-envelope-open-text fa-3x text-secondary mb-3" />
                                <h4>Invitación no disponible</h4>
                                <p className="text-muted">{loadError}</p>
                                <a href="/login" className="btn btn-outline-primary btn-sm">Ir a iniciar sesión</a>
                            </div>
                        )}

                        {invitation && (
                            <>
                                <h3 className="text-center mb-1" style={{ color: accent }}>Únete a {invitation.organization_name}</h3>
                                <p className="text-center text-muted">
                                    {invitation.invited_by ? `${invitation.invited_by} te invitó` : 'Te invitaron'} como <strong>{invitation.role_display}</strong>.
                                </p>
                                <div className="bg-light rounded p-3 mb-4 small text-center">{invitation.email}</div>

                                {invitation.has_account ? (
                                    <>
                                        <p className="small text-muted">Ya tienes una cuenta con este correo. Confirma tu contraseña para unirte.</p>
                                        {(invitation.cases.own > 0 || invitation.cases.monere_open > 0) && (
                                            <div className="alert alert-info small" data-testid="invitation-cases">
                                                {invitation.cases.own > 0 && (
                                                    <div>Las {invitation.cases.own} solicitud(es) de marca que ingresaste para tus clientes pasarán a {invitation.organization_name} contigo.</div>
                                                )}
                                                {invitation.cases.monere_open > 0 && (
                                                    <div>Las {invitation.cases.monere_open} solicitud(es) en curso que Monere te asignó volverán a Monere para que las atienda otro abogado.</div>
                                                )}
                                            </div>
                                        )}
                                        <div className="mb-3">
                                            <InputField label="Contraseña" type="password" value={form.password} required
                                                        onChange={(value) => setForm({ ...form, password: value })}
                                                        onFocus={() => onFocusRemove('password')} error={fieldErrorMessage('password')} />
                                        </div>
                                    </>
                                ) : (
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <InputField label="Nombre" value={form.first_name} required
                                                        onChange={(value) => setForm({ ...form, first_name: value })}
                                                        onFocus={() => onFocusRemove('first_name')} error={fieldErrorMessage('first_name')} />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <InputField label="Apellido" value={form.last_name} required
                                                        onChange={(value) => setForm({ ...form, last_name: value })}
                                                        onFocus={() => onFocusRemove('last_name')} error={fieldErrorMessage('last_name')} />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <InputField label="Crea tu contraseña (mínimo 8 caracteres)" type="password" value={form.password} required
                                                        onChange={(value) => setForm({ ...form, password: value })}
                                                        onFocus={() => onFocusRemove('password')} error={fieldErrorMessage('password')} />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <InputField label="Repetir contraseña" type="password" value={form.password_confirm} required
                                                        onChange={(value) => setForm({ ...form, password_confirm: value })}
                                                        onFocus={() => onFocusRemove('password_confirm')} error={fieldErrorMessage('password_confirm')} />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <SquareCheckbox id="accept_terms" name="accept_terms" value="accept_terms" checked={form.accept_terms}
                                                            onChange={(_v: string, checked: boolean) => { setForm({ ...form, accept_terms: checked }); onFocusRemove('accept_terms'); }}
                                                            label="Acepto los términos del servicio y la política de privacidad." />
                                            <small className="d-block text-muted mt-1">
                                                Lee los <a href="/terminos" target="_blank" rel="noopener noreferrer">términos</a> y
                                                la <a href="/privacidad" target="_blank" rel="noopener noreferrer">política de privacidad</a>.
                                            </small>
                                            {fieldErrorMessage('accept_terms') && <small className="text-danger d-block">{fieldErrorMessage('accept_terms')}</small>}
                                        </div>
                                    </div>
                                )}

                                <ButtonGeneral onClick={handleAccept} title="Aceptar invitación" loading={fetchingAcceptInvitation}
                                               fullWidth icon="fa-check" />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;
